import React from "react";
import { Hooks } from "@xt/xtbase";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

/** 根据接口 valueText（英文）映射到带数值的文案 key */
function mapValueTextToLineKey(valueText) {
  const s = (valueText || "").trim().toLowerCase();
  if (!s) return null;
  if (s.includes("extreme") && s.includes("fear")) return "aicopy.fearGreedExtremeFearWithValue";
  if (s.includes("extreme") && s.includes("greed")) return "aicopy.fearGreedExtremeGreedWithValue";
  if (/\bfear\b/.test(s)) return "aicopy.fearGreedFearWithValue";
  if (/\bgreed\b/.test(s)) return "aicopy.fearGreedGreedWithValue";
  if (s.includes("neutral")) return "aicopy.fearGreedNeutralWithValue";
  return null;
}

/** 中心情绪短文案 key */
function mapValueTextToMoodKey(valueText) {
  const s = (valueText || "").trim().toLowerCase();
  if (!s) return null;
  if (s.includes("extreme") && s.includes("fear")) return "aicopy.fearGreedExtremeFear";
  if (s.includes("extreme") && s.includes("greed")) return "aicopy.fearGreedExtremeGreed";
  if (/\bfear\b/.test(s)) return "aicopy.fearGreedFear";
  if (/\bgreed\b/.test(s)) return "aicopy.fearGreedGreed";
  if (s.includes("neutral")) return "aicopy.fearGreedNeutral";
  return null;
}

/** 0–100 指数对应侧栏圆点色（与常见恐惧贪婪色谱一致） */
function dotColorForValue(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "#848e9c";
  if (n <= 25) return "#f6465d";
  if (n <= 45) return "#f8d33a";
  if (n <= 55) return "#848e9c";
  if (n <= 75) return "#7fd6a0";
  return "#2ebd85";
}

function clampFearGreedValue(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, n));
}

function getIndicatorPosition(val) {
  const n = clampFearGreedValue(val);
  if (n == null) {
    return { x: 8, y: 56 };
  }
  const centerX = 70;
  const centerY = 62;
  const radius = 56;
  const angle = Math.PI - (n / 100) * Math.PI;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY - radius * Math.sin(angle),
  };
}

function moodKeyForValue(val) {
  const n = clampFearGreedValue(val);
  if (n == null) return null;
  if (n <= 25) return "aicopy.fearGreedExtremeFear";
  if (n <= 45) return "aicopy.fearGreedFear";
  if (n <= 55) return "aicopy.fearGreedNeutral";
  if (n <= 75) return "aicopy.fearGreedGreed";
  return "aicopy.fearGreedExtremeGreed";
}

function buildArcPath(cx, cy, r, startDeg, endDeg, steps = 24) {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const progress = i / steps;
    const angleDeg = startDeg + (endDeg - startDeg) * progress;
    const angle = (angleDeg * Math.PI) / 180;
    points.push({
      x: cx + r * Math.cos(angle),
      y: cy - r * Math.sin(angle),
    });
  }
  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

function buildGaugeSegments() {
  const colors = ["#D51730", "#F6465D", "#F8D33A", "#2FE49E", "#2EBD85"];
  const totalSpan = 180;
  const gapDeg = 9;
  const visibleSpan = totalSpan - gapDeg * (colors.length - 1);
  const segmentSpan = visibleSpan / colors.length;

  return colors.map((color, index) => {
    const start = 180 - index * (segmentSpan + gapDeg);
    const end = start - segmentSpan;
    return { start, end, color };
  });
}

/**
 * AI 实验室 — Fear & Greed 卡片
 * @param {{ current?: object, sevenDaysAgo?: object, thirtyDaysAgo?: object } | null} data — GET /public/ai-lab/fear-greed
 * @param {boolean} loading
 */
export default function AiLabFearGreedCard({
  data = null,
  loading = false,
  activeDotIndex = 0,
  dotCount = 4,
}) {
  const t = useTranslation();

  const current = data?.current;
  const week = data?.sevenDaysAgo;
  const month = data?.thirtyDaysAgo;
  const currentValueRaw = current?.value != null ? Number(current.value) : null;
  const weekValueRaw = week?.value != null ? Number(week.value) : null;
  const monthValueRaw = month?.value != null ? Number(month.value) : null;
  const effectiveCurrentValue = currentValueRaw;
  const effectiveWeekValue = weekValueRaw;
  const effectiveMonthValue = monthValueRaw;

  const value =
    effectiveCurrentValue != null && Number.isFinite(effectiveCurrentValue)
      ? Math.round(effectiveCurrentValue)
      : null;
  const moodKey = mapValueTextToMoodKey(current?.valueText) || moodKeyForValue(effectiveCurrentValue);
  const indicatorPos = getIndicatorPosition(effectiveCurrentValue);
  const gaugeSegments = buildGaugeSegments();

  const formatSideLine = (snap, mockedValue) => {
    if (!snap || mockedValue == null) return "—";
    const n = Math.round(Number(mockedValue));
    const lineKey = mapValueTextToLineKey(snap.valueText);
    if (lineKey) return t(lineKey, { n });
    return `${snap.valueText || ""} ${n}`.trim();
  };

  return (
    <div className={`${styles.card} ${loading ? styles.cardLoading : ""}`}>
      <div className={styles.title}>{t("aicopy.fearGreed")}</div>

      <div className={styles.body}>
        <div className={styles.gaugeWrap}>
          <div className={styles.gaugeInner}>
            <svg className={styles.gaugeSvg} viewBox="0 0 140 70" aria-hidden>
              {gaugeSegments.map((segment, index) => (
                <path
                  key={index}
                  d={buildArcPath(70, 62, 56, segment.start, segment.end)}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
              <circle
                className={styles.indicator}
                cx={indicatorPos.x}
                cy={indicatorPos.y}
                r="8"
              />
            </svg>
            <div className={styles.value}>
              {loading ? "…" : value != null ? value : "—"}
            </div>
            <div className={styles.mood}>
              {loading ? "…" : moodKey ? t(moodKey) : "—"}
            </div>
          </div>
        </div>

        <div className={styles.side}>
          <div className={styles.sideBlock}>
            <div className={styles.sideLabel}>{t("aicopy.fearGreedLastWeek")}</div>
            <div className={styles.sideRow}>
              <span
                className={styles.dot}
                style={{
                  background: dotColorForValue(effectiveWeekValue),
                }}
              />
              <span className={styles.sideValue}>{formatSideLine(week, effectiveWeekValue)}</span>
            </div>
          </div>
          <div className={styles.sideBlock}>
            <div className={styles.sideLabel}>{t("aicopy.fearGreedLastMonth")}</div>
            <div className={styles.sideRow}>
              <span
                className={styles.dot}
                style={{
                  background: dotColorForValue(effectiveMonthValue),
                }}
              />
              <span className={styles.sideValue}>{formatSideLine(month, effectiveMonthValue)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* <div className={styles.pagination} aria-hidden>
        {Array.from({ length: dotCount }).map((_, i) => (
          <span
            key={i}
            className={i === activeDotIndex ? styles.pageDotActive : styles.pageDot}
          />
        ))}
      </div> */}
    </div>
  );
}
